import { Injectable, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Purchases, LOG_LEVEL, CustomerInfo, PurchasesOfferings, PurchasesPackage } from '@revenuecat/purchases-capacitor';
import { Purchases as PurchasesWeb, Package as WebPackage, Offering as WebOffering, CustomerInfo as WebCustomerInfo } from '@revenuecat/purchases-js';
import { BehaviorSubject } from 'rxjs';
import { UserService } from '../user/user.service';
import { environment } from '../../../environments/environment';

/**
 * Entitlement identifier configured in RevenueCat dashboard
 * This should match your entitlement ID in RevenueCat
 */
const PREMIUM_ENTITLEMENT_ID = 'Reef Tracker Pro';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  private platform = inject(Platform);
  private userService = inject(UserService);

  private isConfigured = false;
  private isNative = Capacitor.isNativePlatform();
  private webPurchasesInstance: PurchasesWeb | null = null;

  private customerInfoSubject = new BehaviorSubject<CustomerInfo | WebCustomerInfo | null>(null);
  public customerInfo$ = this.customerInfoSubject.asObservable();

  private isPremiumSubject = new BehaviorSubject<boolean>(false);
  public isPremium$ = this.isPremiumSubject.asObservable();

  constructor() {
    // Subscribe to user profile changes to update premium status
    this.userService.userProfile$.subscribe(profile => {
      if (profile?.isGrandfathered) {
        // Grandfathered users always have premium access
        this.isPremiumSubject.next(true);
      } else if (profile && !this.isConfigured) {
        // If not grandfathered and RevenueCat not configured yet, default to free
        // (RevenueCat will update this when configured)
        this.isPremiumSubject.next(false);
      }
    });
  }

  /**
   * Initialize RevenueCat with the user's Firebase UID
   * Call this after the user logs in
   */
  async configure(userId: string): Promise<void> {
    if (this.isConfigured) {
      return;
    }

    // Check if user is grandfathered first
    const profile = this.userService.getCachedProfile();
    if (profile?.isGrandfathered) {
      this.isPremiumSubject.next(true);
      // Still configure RevenueCat for analytics, but user won't need to purchase
    }

    try {
      if (this.isNative) {
        // Native platform: use Capacitor SDK
        await this.configureNative(userId, profile);
      } else {
        // Web platform: use Web SDK (purchases-js)
        await this.configureWeb(userId);
      }

      this.isConfigured = true;

      // Get initial customer info
      await this.refreshCustomerInfo();
    } catch (error) {
      console.error('Failed to configure RevenueCat:', error);
    }
  }

  /**
   * Configure RevenueCat for native platforms (iOS/Android)
   */
  private async configureNative(userId: string, profile: { isGrandfathered?: boolean; grandfatheredAt?: number } | null): Promise<void> {
    // Set debug log level in development
    if (!environment.production) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
    }

    // Configure RevenueCat with platform-specific API key
    await Purchases.configure({
      apiKey: this.getApiKey(),
      appUserID: userId,
    });

    // Set subscriber attributes for grandfathered users
    if (profile?.isGrandfathered) {
      await Purchases.setAttributes({
        $isGrandfathered: 'true',
        $grandfatheredAt: profile.grandfatheredAt?.toString() || Date.now().toString(),
      });
    }
  }

  /**
   * Configure RevenueCat for web platform
   */
  private async configureWeb(userId: string): Promise<void> {
    const apiKey = environment.revenueCat?.webApiKey;
    if (!apiKey || apiKey === 'YOUR_REVENUECAT_WEB_PUBLIC_API_KEY' || apiKey === 'YOUR_REVENUECAT_WEB_SANDBOX_API_KEY') {
      console.warn('RevenueCat Web API key not configured. Web subscriptions will not work.');
      return;
    }

    this.webPurchasesInstance = PurchasesWeb.configure(apiKey, userId);
  }

  /**
   * Get the platform-specific RevenueCat API key (for native platforms only)
   */
  private getApiKey(): string {
    if (this.platform.is('ios')) {
      return environment.revenueCat?.iosApiKey || 'YOUR_IOS_API_KEY';
    } else if (this.platform.is('android')) {
      return environment.revenueCat?.androidApiKey || 'YOUR_ANDROID_API_KEY';
    }
    return environment.revenueCat?.iosApiKey || 'YOUR_IOS_API_KEY';
  }

  /**
   * Refresh customer info from RevenueCat
   */
  async refreshCustomerInfo(): Promise<CustomerInfo | WebCustomerInfo | null> {
    // Check grandfathered status first
    if (this.userService.isGrandfathered()) {
      this.isPremiumSubject.next(true);
      return null;
    }

    try {
      if (this.isNative) {
        const { customerInfo } = await Purchases.getCustomerInfo();
        this.customerInfoSubject.next(customerInfo);
        this.updatePremiumStatus(customerInfo);
        return customerInfo;
      } else if (this.webPurchasesInstance) {
        const customerInfo = await this.webPurchasesInstance.getCustomerInfo();
        this.customerInfoSubject.next(customerInfo);
        this.updatePremiumStatus(customerInfo);
        return customerInfo;
      }
      return null;
    } catch (error) {
      console.error('Failed to get customer info:', error);
      this.isPremiumSubject.next(false);
      return null;
    }
  }

  /**
   * Update premium status based on customer info
   */
  private updatePremiumStatus(customerInfo: CustomerInfo | WebCustomerInfo): void {
    if (this.userService.isGrandfathered()) {
      this.isPremiumSubject.next(true);
      return;
    }

    const hasPremium = customerInfo.entitlements.active[PREMIUM_ENTITLEMENT_ID] !== undefined;
    this.isPremiumSubject.next(hasPremium);
  }

  /**
   * Get available offerings/packages for purchase
   * Returns a unified format that works for both native and web
   */
  async getOfferings(): Promise<PurchasesOfferings | { current: WebOffering | null; all: Record<string, WebOffering> } | null> {
    try {
      if (this.isNative) {
        const offerings = await Purchases.getOfferings();
        return offerings;
      } else if (this.webPurchasesInstance) {
        const offerings = await this.webPurchasesInstance.getOfferings();
        return offerings;
      }
      return null;
    } catch (error) {
      console.error('Failed to get offerings:', error);
      return null;
    }
  }

  /**
   * Purchase a package (native platforms)
   */
  async purchasePackage(pkg: PurchasesPackage): Promise<boolean> {
    if (!this.isNative) {
      console.error('purchasePackage is for native platforms only. Use purchaseWebPackage for web.');
      return false;
    }

    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      this.customerInfoSubject.next(customerInfo);
      this.updatePremiumStatus(customerInfo);

      if (this.userService.userId) {
        await this.userService.updateSubscriptionStatus(this.userService.userId, 'premium');
      }

      return true;
    } catch (error: any) {
      if (error.userCancelled) {
        console.log('User cancelled purchase');
      } else {
        console.error('Purchase failed:', error);
      }
      return false;
    }
  }

  /**
   * Purchase a package (web platform)
   * Opens RevenueCat's web checkout flow powered by Stripe
   */
  async purchaseWebPackage(pkg: WebPackage, htmlTarget?: HTMLElement): Promise<boolean> {
    console.log('SubscriptionService.purchaseWebPackage called');
    console.log('isNative:', this.isNative);
    console.log('webPurchasesInstance:', this.webPurchasesInstance);
    console.log('pkg:', pkg);
    console.log('htmlTarget:', htmlTarget);

    if (this.isNative) {
      console.error('purchaseWebPackage is for web platform only. Use purchasePackage for native.');
      return false;
    }

    if (!this.webPurchasesInstance) {
      console.error('RevenueCat Web SDK not configured');
      return false;
    }

    try {
      const purchaseParams: { rcPackage: WebPackage; htmlTarget?: HTMLElement } = { rcPackage: pkg };
      if (htmlTarget) {
        purchaseParams.htmlTarget = htmlTarget;
      }
      console.log('Calling webPurchasesInstance.purchase with params:', purchaseParams);

      const { customerInfo } = await this.webPurchasesInstance.purchase(purchaseParams);
      console.log('Purchase successful, customerInfo:', customerInfo);
      this.customerInfoSubject.next(customerInfo);
      this.updatePremiumStatus(customerInfo);

      if (this.userService.userId) {
        await this.userService.updateSubscriptionStatus(this.userService.userId, 'premium');
      }

      return true;
    } catch (error: any) {
      // Check for user cancellation using PurchasesError from web SDK
      if (error?.errorCode === 'USER_CANCELLED') {
        console.log('User cancelled purchase');
      } else {
        console.error('Purchase failed:', error);
      }
      return false;
    }
  }

  /**
   * Restore previous purchases (native platforms only)
   * Web purchases are automatically synced via the user's account
   */
  async restorePurchases(): Promise<boolean> {
    if (!this.isNative) {
      // On web, just refresh customer info - purchases are tied to the account
      await this.refreshCustomerInfo();
      return this.hasPremiumAccess();
    }

    try {
      const { customerInfo } = await Purchases.restorePurchases();
      this.customerInfoSubject.next(customerInfo);
      this.updatePremiumStatus(customerInfo);

      const hasPremium = customerInfo.entitlements.active[PREMIUM_ENTITLEMENT_ID] !== undefined;
      if (hasPremium && this.userService.userId) {
        await this.userService.updateSubscriptionStatus(this.userService.userId, 'premium');
      }

      return hasPremium;
    } catch (error) {
      console.error('Failed to restore purchases:', error);
      return false;
    }
  }

  /**
   * Check if the current user has premium access
   * Combines grandfathered status and RevenueCat entitlements
   */
  hasPremiumAccess(): boolean {
    return this.isPremiumSubject.getValue();
  }

  /**
   * Check if RevenueCat is configured
   */
  isReady(): boolean {
    return this.isConfigured;
  }

  /**
   * Check if running on web platform
   */
  isWebPlatform(): boolean {
    return !this.isNative;
  }

  /**
   * Get the web purchases instance (for advanced web-specific operations)
   */
  getWebPurchasesInstance(): PurchasesWeb | null {
    return this.webPurchasesInstance;
  }

  /**
   * Log out - call when user signs out
   */
  async logOut(): Promise<void> {
    if (this.isConfigured) {
      try {
        if (this.isNative) {
          await Purchases.logOut();
        }
        // Web SDK doesn't have a logOut method - just clear local state
        this.webPurchasesInstance = null;
        this.isConfigured = false;
        this.customerInfoSubject.next(null);
        this.isPremiumSubject.next(false);
      } catch (error) {
        console.error('Failed to log out of RevenueCat:', error);
      }
    }
  }
}

